/**
 * EtiquetasPage — fila de impressão de etiquetas (térmica 80 mm ou folha A4).
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Search, Printer, Trash2, X, Package, Plus, Minus, Settings2 } from 'lucide-react';
import { productService } from '../../../../services/erpApi';
import { useToast } from '../../../../hooks/useToast';
import Toast from '../../../../components/Toast/Toast';
import BarcodeLabel from './BarcodeLabel';
import { resolveBarcodeFormat, getPrimaryReference } from '../../../../utils/barcodeLabel';
import './EtiquetasPage.css';

const PAPER_THERMAL = 'thermal80';
const PAPER_A4 = 'a4';

/** Largura útil A4 (~210mm − margens 10mm) */
const A4_USABLE_WIDTH_MM = 190;
const A4_GAP_MM = 3;

const A4_HEIGHT_PRESETS = [
  { id: '40', label: '40 mm', heightMm: 40 },
  { id: '45', label: '45 mm', heightMm: 45 },
  { id: '50', label: '50 mm', heightMm: 50 },
  { id: '55', label: '55 mm', heightMm: 55 },
];

function calcA4LabelWidth(columns) {
  const cols = Math.max(1, Math.min(3, Number(columns) || 1));
  const gaps = A4_GAP_MM * (cols - 1);
  return Math.floor(((A4_USABLE_WIDTH_MM - gaps) / cols) * 10) / 10;
}

function EtiquetasPage() {
  const { toast, showToast, hideToast } = useToast();
  const [searchTerm, setSearchTerm] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [queue, setQueue] = useState([]);
  const [adding, setAdding] = useState(false);
  const [paperFormat, setPaperFormat] = useState(PAPER_THERMAL);
  const [a4Columns, setA4Columns] = useState(2);
  const [a4HeightPreset, setA4HeightPreset] = useState('45');
  const searchWrapRef = useRef(null);
  const inputRef = useRef(null);

  const isA4 = paperFormat === PAPER_A4;

  const labelSize = useMemo(() => {
    if (!isA4) {
      return { widthMm: 80, heightMm: 45 };
    }
    const preset = A4_HEIGHT_PRESETS.find((p) => p.id === a4HeightPreset) || A4_HEIGHT_PRESETS[1];
    return {
      widthMm: calcA4LabelWidth(a4Columns),
      heightMm: preset.heightMm,
    };
  }, [isA4, a4Columns, a4HeightPreset]);

  const totalLabels = useMemo(
    () => queue.reduce((sum, item) => sum + (Number(item.quantity) || 0), 0),
    [queue]
  );

  const printItems = useMemo(() => {
    const items = [];
    queue.forEach((row) => {
      const qty = Math.max(0, Math.floor(Number(row.quantity) || 0));
      for (let i = 0; i < qty; i++) {
        items.push({
          key: `${row.product.id}-${i}`,
          product: row.product,
        });
      }
    });
    return items;
  }, [queue]);

  useEffect(() => {
    const onClickOutside = (e) => {
      if (searchWrapRef.current && !searchWrapRef.current.contains(e.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, []);

  useEffect(() => {
    const trimmed = searchTerm.trim();
    if (trimmed.length < 2) {
      setResults([]);
      setShowDropdown(false);
      return undefined;
    }

    setSearching(true);
    const timeoutId = setTimeout(async () => {
      try {
        const response = await productService.getAll({
          search: trimmed,
          active: true,
          limit: 10,
        });
        const list = response?.success ? (response.data || []) : (Array.isArray(response) ? response : []);
        setResults(list);
        setShowDropdown(list.length > 0);
      } catch (err) {
        console.error('Erro ao buscar produtos:', err);
        setResults([]);
        setShowDropdown(false);
      } finally {
        setSearching(false);
      }
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [searchTerm]);

  useEffect(() => {
    const styleId = 'etiquetas-print-page-size';
    let el = document.getElementById(styleId);
    if (!el) {
      el = document.createElement('style');
      el.id = styleId;
      document.head.appendChild(el);
    }

    el.textContent = isA4
      ? '@media print { @page { size: A4; margin: 0; } html, body { width: auto !important; } }'
      : '@media print { @page { size: 80mm auto; margin: 0; } html, body { width: 80mm !important; } }';

    return () => {
      el?.remove();
    };
  }, [isA4]);

  const addProduct = useCallback(async (productHint) => {
    if (!productHint?.id || adding) return;
    setAdding(true);
    setShowDropdown(false);
    setSearchTerm('');

    try {
      let product = productHint;
      const needsDetail =
        !Array.isArray(productHint.references) ||
        productHint.barcode == null ||
        productHint.barcode === '';

      if (needsDetail) {
        const response = await productService.getById(productHint.id);
        product = response?.success ? response.data : (response?.data || response);
      }

      const barcode = product?.barcode ? String(product.barcode).trim() : '';
      if (!barcode || !resolveBarcodeFormat(barcode)) {
        showToast('Produto sem código de barras cadastrado. Cadastre o EAN antes de imprimir.', 'error');
        return;
      }

      setQueue((prev) => {
        const existing = prev.find((row) => row.product.id === product.id);
        if (existing) {
          return prev.map((row) =>
            row.product.id === product.id
              ? { ...row, product, quantity: row.quantity + 1 }
              : row
          );
        }
        return [...prev, { product, quantity: 1 }];
      });
      showToast('Produto adicionado à fila', 'success');
      inputRef.current?.focus();
    } catch (err) {
      console.error('Erro ao adicionar produto:', err);
      showToast('Erro ao carregar produto', 'error');
    } finally {
      setAdding(false);
    }
  }, [adding, showToast]);

  const handleSearchKeyDown = (e) => {
    if (e.key === 'Enter' && results.length > 0) {
      e.preventDefault();
      addProduct(results[0]);
    }
  };

  const setQuantity = (productId, quantity) => {
    const qty = Math.max(1, Math.floor(Number(quantity) || 1));
    setQueue((prev) =>
      prev.map((row) => (row.product.id === productId ? { ...row, quantity: qty } : row))
    );
  };

  const bumpQuantity = (productId, delta) => {
    setQueue((prev) =>
      prev.map((row) => {
        if (row.product.id !== productId) return row;
        return { ...row, quantity: Math.max(1, row.quantity + delta) };
      })
    );
  };

  const removeFromQueue = (productId) => {
    setQueue((prev) => prev.filter((row) => row.product.id !== productId));
  };

  const clearQueue = () => {
    setQueue([]);
  };

  const handlePrint = () => {
    if (printItems.length === 0) {
      showToast('Adicione produtos à fila antes de imprimir', 'warning');
      return;
    }
    window.print();
  };

  const printRootStyle = isA4
    ? {
        '--label-cols': String(a4Columns),
        '--label-w': `${labelSize.widthMm}mm`,
        '--label-h': `${labelSize.heightMm}mm`,
        '--label-gap': `${A4_GAP_MM}mm`,
      }
    : undefined;

  return (
    <div className="etiquetas-page">
      <div className="page-header no-print">
        <div>
          <h1>
            <Printer size={28} style={{ marginRight: 8, verticalAlign: 'middle' }} />
            Etiquetas
          </h1>
          <p>
            {isA4
              ? `Folha A4 · ${a4Columns} coluna${a4Columns > 1 ? 's' : ''} · ${labelSize.widthMm}×${labelSize.heightMm} mm`
              : 'Monte a fila e imprima na térmica 80 mm (EAN-13).'}
          </p>
        </div>
        <div className="etiquetas-page__header-actions">
          <button
            type="button"
            className="btn-secondary"
            onClick={clearQueue}
            disabled={queue.length === 0}
          >
            <Trash2 size={16} />
            Limpar fila
          </button>
          <button
            type="button"
            className="btn-primary"
            onClick={handlePrint}
            disabled={printItems.length === 0}
          >
            <Printer size={16} />
            Imprimir {totalLabels > 0 ? `${totalLabels} etiqueta${totalLabels === 1 ? '' : 's'}` : ''}
          </button>
        </div>
      </div>

      <section className="etiquetas-settings no-print">
        <div className="etiquetas-settings__title">
          <Settings2 size={16} />
          Configuração de impressão
        </div>
        <div className="etiquetas-settings__row">
          <label className="etiquetas-settings__field">
            <span>Papel</span>
            <select
              value={paperFormat}
              onChange={(e) => setPaperFormat(e.target.value)}
            >
              <option value={PAPER_THERMAL}>Térmica 80 mm</option>
              <option value={PAPER_A4}>Folha A4</option>
            </select>
          </label>

          {isA4 ? (
            <>
              <label className="etiquetas-settings__field">
                <span>Colunas</span>
                <select
                  value={a4Columns}
                  onChange={(e) => setA4Columns(Number(e.target.value))}
                >
                  <option value={1}>1 coluna (lista)</option>
                  <option value={2}>2 colunas</option>
                  <option value={3}>3 colunas</option>
                </select>
              </label>
              <label className="etiquetas-settings__field">
                <span>Altura da etiqueta</span>
                <select
                  value={a4HeightPreset}
                  onChange={(e) => setA4HeightPreset(e.target.value)}
                >
                  {A4_HEIGHT_PRESETS.map((preset) => (
                    <option key={preset.id} value={preset.id}>
                      {preset.label}
                    </option>
                  ))}
                </select>
              </label>
              <div className="etiquetas-settings__hint">
                Largura automática: <strong>{labelSize.widthMm} mm</strong> (cabe em A4 com{' '}
                {a4Columns} coluna{a4Columns > 1 ? 's' : ''})
              </div>
            </>
          ) : (
            <div className="etiquetas-settings__hint">
              Modo bobina: 1 etiqueta por página (80 × 45 mm).
            </div>
          )}
        </div>
      </section>

      <div className="etiquetas-layout no-print">
        <section className="etiquetas-queue">
          <div className="etiquetas-search" ref={searchWrapRef}>
            <div className="search-input-wrapper">
              <Search className="search-icon" size={18} />
              <input
                ref={inputRef}
                type="text"
                className="search-input"
                placeholder="Buscar por nome, SKU, EAN ou referência…"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onFocus={() => results.length > 0 && setShowDropdown(true)}
                onKeyDown={handleSearchKeyDown}
                autoComplete="off"
              />
              {searchTerm ? (
                <button
                  type="button"
                  className="search-clear"
                  onClick={() => {
                    setSearchTerm('');
                    setResults([]);
                    setShowDropdown(false);
                  }}
                  aria-label="Limpar busca"
                >
                  <X size={16} />
                </button>
              ) : null}
            </div>
            {showDropdown ? (
              <ul className="etiquetas-search__dropdown">
                {searching ? (
                  <li className="etiquetas-search__empty">Buscando…</li>
                ) : (
                  results.map((p) => (
                    <li key={p.id}>
                      <button type="button" onClick={() => addProduct(p)}>
                        <Package size={16} />
                        <span className="etiquetas-search__name">{p.name}</span>
                        <span className="etiquetas-search__meta">
                          SKU {p.sku || '—'}
                          {p.barcode ? ` · ${p.barcode}` : ' · sem EAN'}
                        </span>
                      </button>
                    </li>
                  ))
                )}
              </ul>
            ) : null}
          </div>

          <div className="etiquetas-queue__summary">
            {queue.length} produto{queue.length === 1 ? '' : 's'} · {totalLabels} etiqueta
            {totalLabels === 1 ? '' : 's'}
          </div>

          {queue.length === 0 ? (
            <div className="etiquetas-queue__empty">
              Busque e adicione produtos para montar a fila de impressão.
            </div>
          ) : (
            <div className="etiquetas-queue__table-wrap">
              <table className="etiquetas-queue__table">
                <thead>
                  <tr>
                    <th>Produto</th>
                    <th>SKU</th>
                    <th>EAN</th>
                    <th>REF</th>
                    <th>Qtd</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {queue.map((row) => {
                    const ref = getPrimaryReference(row.product);
                    return (
                      <tr key={row.product.id}>
                        <td className="etiquetas-queue__name">{row.product.name}</td>
                        <td>{row.product.sku || '—'}</td>
                        <td className="etiquetas-queue__ean">{row.product.barcode || '—'}</td>
                        <td>{ref || '—'}</td>
                        <td>
                          <div className="etiquetas-qty">
                            <button
                              type="button"
                              onClick={() => bumpQuantity(row.product.id, -1)}
                              aria-label="Diminuir"
                            >
                              <Minus size={14} />
                            </button>
                            <input
                              type="number"
                              min={1}
                              value={row.quantity}
                              onChange={(e) => setQuantity(row.product.id, e.target.value)}
                            />
                            <button
                              type="button"
                              onClick={() => bumpQuantity(row.product.id, 1)}
                              aria-label="Aumentar"
                            >
                              <Plus size={14} />
                            </button>
                          </div>
                        </td>
                        <td>
                          <button
                            type="button"
                            className="etiquetas-queue__remove"
                            onClick={() => removeFromQueue(row.product.id)}
                            aria-label="Remover"
                          >
                            <Trash2 size={16} />
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </section>

        <section className="etiquetas-preview">
          <h2>Preview {isA4 ? `(A4 · ${a4Columns} col.)` : '(térmica)'}</h2>
          {queue.length === 0 ? (
            <p className="etiquetas-preview__hint">As etiquetas aparecerão aqui.</p>
          ) : (
            <div
              className={`etiquetas-preview__stack ${isA4 ? 'is-a4' : ''}`}
              style={
                isA4
                  ? {
                      '--label-cols': String(a4Columns),
                      '--label-w': `${labelSize.widthMm}mm`,
                      '--label-h': `${labelSize.heightMm}mm`,
                      '--label-gap': `${A4_GAP_MM}mm`,
                    }
                  : undefined
              }
            >
              {(isA4 ? printItems.slice(0, a4Columns * 3) : queue.slice(0, 3)).map((row) => {
                const product = row.product;
                const key = row.key || `preview-${product.id}`;
                const qtyBadge = !isA4 && row.quantity > 1 ? row.quantity : null;
                return (
                  <div key={key} className="etiquetas-preview__item">
                    <BarcodeLabel
                      product={product}
                      widthMm={labelSize.widthMm}
                      heightMm={labelSize.heightMm}
                    />
                    {qtyBadge ? (
                      <span className="etiquetas-preview__badge">×{qtyBadge}</span>
                    ) : null}
                  </div>
                );
              })}
              {isA4 && printItems.length > a4Columns * 3 ? (
                <p className="etiquetas-preview__hint etiquetas-preview__hint--full">
                  +{printItems.length - a4Columns * 3} etiqueta(s) na impressão.
                </p>
              ) : null}
              {!isA4 && queue.length > 3 ? (
                <p className="etiquetas-preview__hint">
                  +{queue.length - 3} produto(s) na fila (só os 3 primeiros no preview).
                </p>
              ) : null}
            </div>
          )}
        </section>
      </div>

      <div
        id="barcode-labels-print"
        className={isA4 ? 'is-a4' : 'is-thermal'}
        data-paper={paperFormat}
        style={printRootStyle}
        aria-hidden={printItems.length === 0}
      >
        {printItems.map((item) => (
          <BarcodeLabel
            key={item.key}
            product={item.product}
            widthMm={labelSize.widthMm}
            heightMm={labelSize.heightMm}
          />
        ))}
      </div>

      {toast.show ? (
        <Toast show message={toast.message} type={toast.type} onClose={hideToast} />
      ) : null}
    </div>
  );
}

export default EtiquetasPage;
